import { PERMANENT_DELETE_CONFIRMATION_TOKEN } from '@/common/constants/user';
import { AppButton } from '@/ui/buttons';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  &.p-dialog {
    width: 450px;
    border-radius: var(--medium-border-radius);

    .p-dialog-header {
      display: none;
    }

    .p-dialog-content {
      padding: var(--semi-big-padding);
      padding-bottom: var(--default-padding);
    }

    .p-dialog-footer {
      padding: var(--semi-big-padding);
      padding-top: var(--default-padding);
      display: flex;
      gap: var(--semi-xsmall-padding);
    }
  }
`;

const StyledInput = styled(InputText)`
  width: 100%;
  background-color: var(--beige-main);
  border: none;
  padding: var(--default-padding);

  &:focus {
    box-shadow: none;
    border: none;
  }
`;

export type PermanentDeleteTarget = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
};

type PermanentDeleteUserModalProps = {
  user: PermanentDeleteTarget | null;
  onConfirm: (userId: string) => void;
  onClose: () => void;
};

export const PermanentDeleteUserModal: React.FC<
  PermanentDeleteUserModalProps
> = ({ user, onConfirm, onClose }) => {
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    if (!user) return;
    setConfirmText('');
  }, [user]);

  const isConfirmed = confirmText === PERMANENT_DELETE_CONFIRMATION_TOKEN;
  const userName = user
    ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
      user.username ||
      ''
    : '';

  return (
    <StyledDialog
      visible={!!user}
      onHide={onClose}
      closable={false}
      draggable={false}
      footer={
        <>
          <AppButton
            label={t('dialog.permanentDelete.user.confirm')}
            severity="danger"
            isDisabled={!isConfirmed}
            onClick={() => {
              if (user && isConfirmed) {
                onConfirm(user.id);
              }
            }}
          />
          <AppButton
            label={t('button.cancel')}
            severity="secondary"
            type="outlined"
            onClick={onClose}
          />
        </>
      }
    >
      <h3
        style={{
          fontSize: 'var(--modal-heading-font-size)',
          fontWeight: 700,
          lineHeight: 1.4,
          margin: 0,
        }}
      >
        {t('dialog.permanentDelete.user.header')}
      </h3>
      <p style={{ marginTop: '6px' }}>
        <Trans
          i18nKey="dialog.permanentDelete.user.body"
          values={{ userName }}
          components={{ bold: <strong /> }}
        />
      </p>
      <p style={{ marginTop: 'var(--default-padding)' }}>
        {t('dialog.permanentDelete.user.typeConfirm', {
          token: PERMANENT_DELETE_CONFIRMATION_TOKEN,
        })}
      </p>
      <StyledInput
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        placeholder={t('dialog.permanentDelete.user.inputPlaceholder')}
        autoFocus
      />
    </StyledDialog>
  );
};
